import { useState } from "react";
import { useInput, FieldTitle, useResourceContext } from "ra-core";
import { FormField, FormLabel } from "@/components/admin/form";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { Markdown } from "./Markdown";

/**
 * Markdown authoring with a preview.
 *
 * Deliberately not a WYSIWYG. The app already renders markdown through
 * Markdown.tsx -- marked plus DOMPurify -- so this needs no editor dependency,
 * and what is stored stays plain text: greppable, diffable, and safe to put in
 * an email or a PDF later. A rich-text editor would store markup that every
 * other consumer then has to sanitise again.
 */
export function MarkdownInput({
  source,
  label,
  helperText,
  rows = 6,
}: {
  source: string;
  label?: string | false;
  helperText?: string | false;
  rows?: number;
}) {
  const resource = useResourceContext();
  const { field, isRequired } = useInput({ source });
  const [preview, setPreview] = useState(false);
  const value = (field.value as string) ?? "";

  return (
    <FormField id={source} name={field.name} className="w-full">
      <div className="flex items-center justify-between">
        {label === false ? null : (
          <FormLabel>
            <FieldTitle
              source={source}
              label={label}
              resource={resource}
              isRequired={isRequired}
            />
          </FormLabel>
        )}
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant={preview ? "ghost" : "secondary"}
            onClick={() => setPreview(false)}
          >
            Write
          </Button>
          <Button
            type="button"
            size="sm"
            variant={preview ? "secondary" : "ghost"}
            onClick={() => setPreview(true)}
            disabled={!value.trim()}
          >
            Preview
          </Button>
        </div>
      </div>

      {preview ? (
        <div
          className={cn(
            "rounded-md border px-3 py-2 text-sm overflow-y-auto",
            "prose-sm max-w-none",
          )}
          style={{ minHeight: `${rows * 1.5}rem` }}
        >
          <Markdown>{value}</Markdown>
        </div>
      ) : (
        <Textarea
          {...field}
          value={value}
          rows={rows}
          placeholder="Markdown supported: **bold**, - lists, [links](https://…)"
        />
      )}

      {helperText === false ? null : (
        <p className="text-xs text-muted-foreground">
          {helperText ?? "Supports markdown. Preview before saving."}
        </p>
      )}
    </FormField>
  );
}
